import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Box, Chip, TextField } from '@mui/material';
import { sendServerRequest } from '../../server/request';
import { URLS } from '../../env/urls';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';

interface ShotDebug {
  id: string;
  provider: string;
  dispatched: boolean;
  reason: string;
  response_data: any;
  createdAt: Date;
}

export function CltShotDebug({ chatId }: { chatId: number }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shots, setShots] = useState<ShotDebug[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <>
      <Button fullWidth variant="text" onClick={load}>Ver debug de disparo de shot</Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Debug de Disparo de Shot CLT</DialogTitle>
        <DialogContent>
          {loading ? (
            <CircularLoading />
          ) : shots.length === 0 ? (
            <p>Sem disparos registrados.</p>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              {shots.map((shot) => (
                <ShotCard
                  key={shot.id}
                  shot={shot}
                  expanded={expanded === shot.id}
                  onToggle={() => setExpanded(expanded === shot.id ? null : shot.id)}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  function load() {
    setOpen(true);
    if (shots.length > 0) return;
    setLoading(true);
    sendServerRequest('GET', URLS.fgtsChat.cltShotDebug(chatId))
      .then((e) => {
        setShots(e.data?.data?.shots || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os dados de debug.', 'error');
      });
  }
}

const REASON_LABELS: Record<string, string> = {
  noCpf: 'Sem CPF',
  recentShot: 'Shot Recente',
  recentChat: 'Chat Recente',
  noConsultData: 'Sem Dados da Consulta',
  noMargin: 'Sem Margem',
  noForm: 'Sem Form',
  noClient: 'Sem Cliente',
  error: 'Erro no Disparo',
};

function ShotCard({ shot, expanded, onToggle }: { shot: ShotDebug; expanded: boolean; onToggle: () => void }) {
  const providerLabel = shot.provider === 'v8' ? 'V8' : 'Suma';
  const statusColor = shot.dispatched ? 'success' : 'error';
  const reasonLabel = shot.dispatched ? '✅ Disparado' : `❌ ${REASON_LABELS[shot.reason] ?? shot.reason}`;

  return (
    <Box
      sx={{
        border: `1px solid ${shot.dispatched ? '#4caf50' : '#f44336'}`,
        borderRadius: 1,
        p: 2,
        cursor: 'pointer',
        '&:hover': { bgcolor: '#f5f5f5' },
        transition: 'all 0.2s',
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <Chip
          label={providerLabel}
          size="small"
          variant="outlined"
        />
        <Chip
          label={reasonLabel}
          size="small"
          color={statusColor}
          variant="outlined"
        />
        <span style={{ fontSize: '0.85em', color: '#666' }}>
          {new Date(shot.createdAt).toLocaleString('pt-BR')}
        </span>
      </Box>

      {expanded && shot.response_data && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
          <div style={{ fontSize: '0.85em' }}>
            <strong>Response:</strong>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={typeof shot.response_data === 'string' ? shot.response_data : JSON.stringify(shot.response_data, null, 2)}
              InputProps={{ readOnly: true }}
              size="small"
              variant="outlined"
              sx={{ mt: 0.5 }}
            />
          </div>
        </Box>
      )}

      {!expanded && shot.response_data && (
        <div style={{ fontSize: '0.85em', color: '#999', marginTop: 8 }}>
          Clique para ver resposta
        </div>
      )}
    </Box>
  );
}
